import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  fetchAuthSession,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { loadAuthConfig } from "./authConfig";

/** An authentication problem with a message that is safe to show to the user */
export class AuthFailure extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = "AuthFailure";
  }
}

let configured: Promise<void> | undefined;
export const ensureAuthConfigured = () => {
  configured ??= loadAuthConfig()
    .then(({ userPoolId, userPoolClientId }) => {
      Amplify.configure({
        Auth: { Cognito: { userPoolId, userPoolClientId } },
      });
    })
    .catch((error) => {
      configured = undefined;
      throw error;
    });
  return configured;
};

const INVALID_CREDENTIALS = "Invalid username or password";

const toAuthFailure = (error: any): AuthFailure => {
  switch (error?.name) {
    case "NotAuthorizedException":
    case "UserNotFoundException":
    case "UserLambdaValidationException":
      return new AuthFailure(INVALID_CREDENTIALS, error.name);
    case "PasswordResetRequiredException":
      return new AuthFailure(
        "Your password must be reset - please request a reset email from your admin",
        error.name
      );
    case "TooManyRequestsException":
    case "LimitExceededException":
      return new AuthFailure("Too many attempts - please try again later", error.name);
    case "CodeMismatchException":
    case "ExpiredCodeException":
      return new AuthFailure(
        "Reset Link Expired - please request a new one from your admin",
        error.name
      );
    case "InvalidPasswordException":
      return new AuthFailure(
        error.message || "Password does not meet the requirements",
        error.name
      );
    default:
      return error;
  }
};

/**
 * Signs in with username and password. USER_PASSWORD_AUTH (rather than the library default, SRP) is what lets
 * Cognito's user-migration trigger verify a not-yet-migrated user's existing password on their first sign-in.
 */
export const signInWithPassword = async (username: string, password: string) => {
  await ensureAuthConfigured();
  try {
    // A stale local session would make signIn refuse with UserAlreadyAuthenticatedException
    await signOut();
    const { isSignedIn } = await signIn({
      username,
      password,
      options: { authFlowType: "USER_PASSWORD_AUTH" },
    });
    if (!isSignedIn) throw new AuthFailure(INVALID_CREDENTIALS);
  } catch (error) {
    throw toAuthFailure(error);
  }
};

export const signOutUser = async () => {
  try {
    await ensureAuthConfigured();
    await signOut();
  } catch {
    // Nothing useful to do: local state is cleared by the caller either way
  }
};

// Concurrent forced refreshes (e.g. several requests failing at once) share one network call
let forcedRefresh: Promise<string | undefined> | undefined;

/**
 * The current ID token, or undefined when there is no valid session. The library refreshes it from the stored
 * refresh token when it is expired or about to expire; `forceRefresh` does so unconditionally.
 */
export const getIdToken = async ({ forceRefresh = false } = {}) => {
  const read = async () => {
    try {
      await ensureAuthConfigured();
      const { tokens } = await fetchAuthSession({ forceRefresh });
      return tokens?.idToken?.toString();
    } catch {
      return undefined;
    }
  };
  if (!forceRefresh) return read();

  forcedRefresh ??= read().finally(() => {
    forcedRefresh = undefined;
  });
  return forcedRefresh;
};

export const confirmPasswordReset = async ({
  username,
  code,
  newPassword,
}: {
  username: string;
  code: string;
  newPassword: string;
}) => {
  await ensureAuthConfigured();
  try {
    await confirmResetPassword({ username, confirmationCode: code, newPassword });
  } catch (error) {
    throw toAuthFailure(error);
  }
};
